import { ObjectRepository, PrimitiveValueRepository, RefBuilder, TextRepository } from '@framework/repository';
import { ViewModelTag } from '@view-model/tags/ViewModelTag';
import type {
  ViewModelIndividualPermissionRoleJSON,
  ViewModelTagId,
  ViewModelTagJSON,
  WorkspaceMemberRoleJSON,
} from 'balus-common';
import { RTDBPath } from 'balus-common';
import { canReadViewModel } from './canReadViewModel';
import { WorkspaceSetting } from '@workspace/domain/workspace';
import { ViewModelSharingUserRoleType } from './vo/ViewModelSharingUserRoleType';
import { HashUtils } from '@framework/utils/HashUtils';

export type PredictiveViewModelTag = {
  id: string;
  name: string;
  assignedViewModelCount: number;
};

export class ViewModelTagOperation {
  /**
   * ViewModelから参照されているタグ一覧を、参照件数（ログインユーザが閲覧権限があるViewModelのみ）
   * と共に取得する
   * また、取得対象のタグ名は入力されたキーワードに部分一致したものに限定する
   *
   * @param groupId グループID
   * @param userId ログインユーザID
   * @param keyword 入力されたキーワード
   * @returns ViewModelから参照されているタグ一覧
   */
  static async predictiveList(groupId: string, userId: string, keyword: string): Promise<PredictiveViewModelTag[]> {
    if (keyword === '') {
      return [];
    }

    const snapshotAssigned = await RefBuilder.ref(
      RTDBPath.ViewModelTags.tagsAssignedViewModelsGroupPath(groupId)
    ).get();
    if (snapshotAssigned.val() === null) {
      return [];
    }

    const snapshot = await RefBuilder.ref(RTDBPath.ViewModelTags.tagsEntitiesPath(groupId)).get();
    if (snapshot.val() === null) {
      return [];
    }

    const assigned: { tagId: string; viewModelIds: string[] }[] = [];
    snapshotAssigned.forEach((assignedTagId) => {
      if (assignedTagId.key !== null) {
        assigned.push({ tagId: assignedTagId.key, viewModelIds: Object.keys(assignedTagId.val()) });
      }
    });

    const assignedAndVisibleViewModelCountMap = await Promise.all(
      assigned.map(async ({ tagId, viewModelIds }) => {
        // 閲覧権限があるViewModelかどうかを判定し、trueの件数をカウントする
        const isViewModelVisibleList = await Promise.all(
          viewModelIds.map((viewModelId) => ViewModelTagOperation.isViewModelVisible(userId, groupId, viewModelId))
        );

        return {
          tagId,
          assignedViewModelCount: isViewModelVisibleList.filter((v) => v).length,
        };
      })
    );

    // 情報をマージする
    const tags: PredictiveViewModelTag[] = [];
    const keywordInLowerCase = keyword.toLowerCase();

    snapshot.forEach((tag) => {
      const data = tag.val() as ViewModelTagJSON;
      const tagNameInLowerCase = data.name.toLowerCase();
      if (tagNameInLowerCase.indexOf(keywordInLowerCase) !== -1) {
        // キーワードに部分一致したものを対象とする（大文字小文字の違いは許容）
        const found = assignedAndVisibleViewModelCountMap.find(({ tagId }) => tagId === data.id);
        const assignedViewModelCount = found?.assignedViewModelCount ?? 0;

        tags.push({ id: data.id, name: data.name, assignedViewModelCount });
      }
    });

    // 件数の降順でソート
    return tags.sort((a, b) => b.assignedViewModelCount - a.assignedViewModelCount);
  }

  /**
   * 条件に一致するタグ情報を検索する
   *
   * @param groupId グループID
   * @param viewModelTagId タグID
   * @returns タグ情報
   */
  static async find(groupId: string, viewModelTagId: string): Promise<ViewModelTagJSON | null> {
    const snapshot = await RefBuilder.ref(RTDBPath.ViewModelTags.tagsEntityPath(groupId, viewModelTagId)).get();
    return snapshot.val() as ViewModelTagJSON;
  }

  private static async findIdByName(groupId: string, tagName: string): Promise<ViewModelTagId | null> {
    const path = RTDBPath.ViewModelTags.tagsUniqueIndexPath(groupId, await HashUtils.hash(tagName.trim()));
    const snapshot = await RefBuilder.ref(path).get();
    return snapshot.val() as ViewModelTagId;
  }

  /**
   * タグを削除する
   *
   * @param groupId グループID
   * @param viewModelTagId タグID
   */
  static async remove(groupId: string, viewModelTagId: string): Promise<void> {
    const tag = await ViewModelTagOperation.find(groupId, viewModelTagId);
    if (tag === null) {
      throw new Error('Target tag not found');
    }

    // インデックスや紐付け情報も合わせて削除する
    const saveValues = {
      [RTDBPath.ViewModelTags.tagsEntityPath(groupId, tag.id)]: null,
      [RTDBPath.ViewModelTags.tagsUniqueIndexPath(groupId, tag.hashedName)]: null,
      [RTDBPath.ViewModelTags.tagsAssignedViewModelsPath(groupId, tag.id)]: null,
    };

    await RefBuilder.ref().update(saveValues);
  }

  /**
   * タグ名称を更新する
   *
   * @param groupId グループID
   * @param viewModelTagId タグID
   * @param tagName タグ名称
   * @returns DBに更新されたタグ名称/null
   */
  static async updateName(groupId: string, viewModelTagId: string, tagName: string): Promise<string | null> {
    const tag = await ViewModelTagOperation.find(groupId, viewModelTagId);
    if (tag === null) {
      throw new Error('Target tag not found');
    }

    const trimmedTagName = tagName.trim();
    if (tag.name === trimmedTagName) {
      // 変化なし
      return null;
    }

    await ViewModelTagOperation.saveTagEntityAndIndex(
      groupId,
      await ViewModelTag.fromOther(tag, trimmedTagName),
      tag.hashedName
    );

    return trimmedTagName;
  }

  private static getRepos = (currentUserId: string, groupId: string, workspaceId: string, viewModelId: string) => {
    const workspaceMemberRoleRepo = new TextRepository<WorkspaceMemberRoleJSON>(
      RTDBPath.Workspace.memberRolePath(workspaceId, currentUserId)
    );
    const workspaceSettingRepo = new ObjectRepository(WorkspaceSetting, RTDBPath.Workspace.settingPath(workspaceId));
    const sharingUserRoleRepo = new TextRepository(RTDBPath.ViewModel.sharingUserRolePath(viewModelId));
    const groupEnableIndividualViewModelPermissionsRepo = new PrimitiveValueRepository<boolean>(
      RTDBPath.Group.enableIndividualViewModelPermissionsPath(groupId)
    );
    const viewModelTrashedAtRepo = new TextRepository(RTDBPath.ViewModel.trashedAtPath(viewModelId));
    const individualPermissionRoleRepo = new TextRepository<ViewModelIndividualPermissionRoleJSON>(
      RTDBPath.ViewModel.individualPermissionPath(viewModelId, currentUserId)
    );

    return {
      workspaceMemberRoleRepo,
      workspaceSettingRepo,
      sharingUserRoleRepo,
      groupEnableIndividualViewModelPermissionsRepo,
      viewModelTrashedAtRepo,
      individualPermissionRoleRepo,
    };
  };

  static async isViewModelVisible(userId: string, groupId: string, viewModelId: string): Promise<boolean> {
    // ビューモデルに紐づくワークスペースID
    const workspaceIdRepo = new TextRepository(RTDBPath.ViewModel.viewModelWorkspaceIdPath(viewModelId));
    const workspaceId = await workspaceIdRepo.get();
    if (workspaceId === null) {
      return false;
    }

    const {
      workspaceMemberRoleRepo,
      workspaceSettingRepo,
      sharingUserRoleRepo,
      groupEnableIndividualViewModelPermissionsRepo,
      viewModelTrashedAtRepo,
      individualPermissionRoleRepo,
    } = ViewModelTagOperation.getRepos(userId, groupId, workspaceId, viewModelId);

    const groupEnableIndividualViewModelPermissions =
      (await groupEnableIndividualViewModelPermissionsRepo.get()) || false;
    const workspaceMemberRole = workspaceMemberRoleRepo ? await workspaceMemberRoleRepo.get() : null;
    const workspaceSetting = await workspaceSettingRepo.get();
    const viewModelTrashedAt = await viewModelTrashedAtRepo.get();
    const sharingUserRole = await sharingUserRoleRepo.get();
    const individualPermissionRole = individualPermissionRoleRepo ? await individualPermissionRoleRepo.get() : null;

    // ビューモデルの可視性判定に必要な情報を渡し、共通処理で判定する
    return (
      workspaceSetting !== null &&
      sharingUserRole !== null &&
      canReadViewModel({
        workspaceSetting,
        workspaceMemberRole,
        viewModelTrashedAt,
        viewModelSharingUserRoleType: ViewModelSharingUserRoleType.load(sharingUserRole),
        groupEnableIndividualViewModelPermissions,
        individualPermissionRole,
      })
    );
  }

  /**
   * タグとビューモデルを紐付ける
   *
   * @param groupId グループID
   * @param viewModelId ビューモデルID
   * @param tagName タグ名称
   * @returns タグID
   */
  static async assign(groupId: string, viewModelId: string, tagName: string): Promise<string> {
    let tagId = await ViewModelTagOperation.findIdByName(groupId, tagName);
    if (tagId === null) {
      // 同名のタグがなければ新規登録する
      const tag = await ViewModelTag.buildNew(tagName.trim());
      tagId = await ViewModelTagOperation.saveTagEntityAndIndex(groupId, tag);
    }
    const path = RTDBPath.ViewModelTags.tagsAssignedViewModelPath(groupId, tagId, viewModelId);
    await RefBuilder.ref().update({ [path]: 'N/A' });

    return tagId;
  }

  /**
   * タグとビューモデルの紐付けを解除する
   *
   * @param groupId グループID
   * @param tagId タグID
   * @param viewModelId ビューモデルID
   */
  static async unassign(groupId: string, tagId: string, viewModelId: string) {
    const path = RTDBPath.ViewModelTags.tagsAssignedViewModelPath(groupId, tagId, viewModelId);
    await RefBuilder.ref(path).remove();
  }

  /**
   * タグ情報を保存する
   *
   * @param groupId グループID
   * @param tag タグ情報
   * @param oldHashedName 古いハッシュ化されたタグ名称（これが指定された場合は更新）
   * @returns タグID
   */
  private static async saveTagEntityAndIndex(
    groupId: string,
    tag: ViewModelTag,
    oldHashedName?: string
  ): Promise<string> {
    const saveValues = {
      [RTDBPath.ViewModelTags.tagsEntityPath(groupId, tag.id)]: tag,
      [RTDBPath.ViewModelTags.tagsUniqueIndexPath(groupId, tag.hashedName)]: tag.id,
    };

    // 更新の場合は、古いインデックスを消す
    await RefBuilder.ref().update({
      ...saveValues,
      ...(oldHashedName
        ? {
            [RTDBPath.ViewModelTags.tagsUniqueIndexPath(groupId, oldHashedName)]: null,
          }
        : {}),
    });
    return tag.id;
  }
}
